<template>
	<div v-if="isDebugMode" data-test="snapshot-source-debug" class="text-body-2 text-medium-emphasis">
		<div v-if="loading" class="d-flex align-center ga-2">
			<v-progress-circular indeterminate size="16" width="2" />
			<span>Loading recording source…</span>
		</div>
		<span v-else-if="loadError" data-test="snapshot-source-error">{{ loadError }}</span>
		<span v-else-if="!source" data-test="snapshot-source-missing">No recording source metadata</span>
		<div v-else class="d-flex align-center ga-2 flex-wrap">
			<span>Source:</span>
			<span data-test="snapshot-source-offset">{{ formatOffsetMs(source.sourceMasterRecordingOffsetMs) }}</span>
			<span aria-hidden="true">·</span>
			<span data-test="snapshot-source-id" class="text-truncate">{{ source.sourceMasterRecordingId }}</span>
			<v-menu
				v-model="previewOpen"
				:close-on-content-click="false"
				location="top start"
				@update:model-value="onPreviewVisibilityChanged">
				<template #activator="{ props: menuProps }">
					<v-btn v-bind="menuProps" data-test="preview-source" size="x-small" variant="tonal"> Preview source </v-btn>
				</template>
				<v-card class="pa-3 source-preview-card">
					<div class="text-caption mb-2">Target: {{ formatOffsetMs(source.sourceMasterRecordingOffsetMs) }}</div>
					<div v-if="previewLoading" class="d-flex align-center justify-center source-preview-video">
						<v-progress-circular indeterminate size="24" width="2" />
					</div>
					<v-alert v-else-if="previewError" data-test="source-preview-error" type="error" density="compact" variant="tonal">
						{{ previewError }}
					</v-alert>
					<template v-else-if="previewUrl">
						<v-alert
							v-if="previewWarning"
							data-test="source-preview-warning"
							type="warning"
							density="compact"
							variant="tonal"
							class="mb-2">
							{{ previewWarning }}
						</v-alert>
						<video
							ref="previewVideo"
							class="source-preview-video"
							:src="previewUrl"
							controls
							preload="metadata"
							@loadedmetadata="seekToSourceOffset"
							@seeked="pausePreview"
							@error="previewError = 'Unable to play source recording'"></video>
					</template>
				</v-card>
			</v-menu>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import api from "@/api";
	import { getMessageFromError } from "@/helpers/utilities";

	interface SnapshotSource {
		sourceMasterRecordingId: string;
		sourceMasterRecordingOffsetMs: number;
	}

	interface SnapshotSourceResponse {
		id: string;
		sourceMasterRecordingId?: string | null;
		sourceMasterRecordingOffsetMs?: number | null;
	}

	interface MasterRecordingResponse {
		privateUrl?: string | null;
	}

	const props = defineProps<{
		snapshotId: string;
	}>();

	const isDebugMode = computed(() => new URLSearchParams(window.location.search).get("debug") === "true");
	const loading = ref(false);
	const loadError = ref("");
	const source = ref<SnapshotSource | null>(null);
	const previewOpen = ref(false);
	const previewLoading = ref(false);
	const previewUrl = ref("");
	const previewError = ref("");
	const previewWarning = ref("");
	const previewVideo = ref<HTMLVideoElement | null>(null);
	let requestSequence = 0;
	let previewRequestSequence = 0;

	function formatOffsetMs(offsetMs: number) {
		const totalSeconds = Math.floor(offsetMs / 1000);
		const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
		const seconds = String(totalSeconds % 60).padStart(2, "0");
		const milliseconds = String(offsetMs % 1000).padStart(3, "0");
		return `${minutes}:${seconds}.${milliseconds}`;
	}

	async function onPreviewVisibilityChanged(open: boolean) {
		if (!open) {
			previewRequestSequence++;
			pausePreview();
			return;
		}
		if (!source.value) return;

		const sequence = ++previewRequestSequence;
		previewLoading.value = true;
		previewUrl.value = "";
		previewError.value = "";
		previewWarning.value = "";
		try {
			const recording = (await api.get(
				`/master-recordings/${encodeURIComponent(source.value.sourceMasterRecordingId)}`
			)) as MasterRecordingResponse;
			if (sequence !== previewRequestSequence) return;
			if (!recording.privateUrl) {
				throw new Error("Missing privateUrl");
			}
			previewUrl.value = recording.privateUrl;
		} catch (error) {
			if (sequence === previewRequestSequence) {
				console.error("Failed to load snapshot source recording preview.", error);
				previewError.value = `Unable to load source recording: ${getMessageFromError(error)}`;
			}
		} finally {
			if (sequence === previewRequestSequence) {
				previewLoading.value = false;
			}
		}
	}

	function seekToSourceOffset(event: Event) {
		if (!source.value) return;
		const video = event.currentTarget as HTMLVideoElement;
		const targetSeconds = source.value.sourceMasterRecordingOffsetMs / 1000;
		if (Number.isFinite(video.duration) && source.value.sourceMasterRecordingOffsetMs > Math.round(video.duration * 1000)) {
			previewWarning.value = `Stored offset ${formatOffsetMs(
				source.value.sourceMasterRecordingOffsetMs
			)} exceeds video duration ${formatOffsetMs(Math.round(video.duration * 1000))}`;
			video.currentTime = video.duration;
		} else {
			video.currentTime = targetSeconds;
		}
		video.pause();
	}

	function pausePreview() {
		previewVideo.value?.pause();
	}

	watch(
		[() => props.snapshotId, isDebugMode],
		async ([snapshotId, debugMode]) => {
			const sequence = ++requestSequence;
			previewOpen.value = false;
			previewRequestSequence++;
			previewUrl.value = "";
			previewError.value = "";
			previewWarning.value = "";
			pausePreview();
			source.value = null;
			loadError.value = "";
			loading.value = false;
			if (!debugMode || !snapshotId) return;

			loading.value = true;
			try {
				const snapshots = (await api.getSnapshots({
					where: { id: snapshotId },
					pageSize: 1
				})) as SnapshotSourceResponse[];
				if (sequence !== requestSequence) return;

				const snapshot = snapshots[0];
				if (
					typeof snapshot?.sourceMasterRecordingId === "string" &&
					Number.isSafeInteger(snapshot.sourceMasterRecordingOffsetMs) &&
					(snapshot.sourceMasterRecordingOffsetMs ?? -1) >= 0
				) {
					source.value = {
						sourceMasterRecordingId: snapshot.sourceMasterRecordingId,
						sourceMasterRecordingOffsetMs: snapshot.sourceMasterRecordingOffsetMs as number
					};
				}
			} catch (error) {
				if (sequence === requestSequence) {
					console.error("Failed to load snapshot recording source metadata.", error);
					loadError.value = `Unable to load recording source metadata: ${getMessageFromError(error)}`;
				}
			} finally {
				if (sequence === requestSequence) {
					loading.value = false;
				}
			}
		},
		{ immediate: true }
	);
</script>

<style scoped>
	.source-preview-card {
		width: min(400px, calc(100vw - 32px));
	}

	.source-preview-video {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
		background: #000;
	}
</style>
